/**
 * A wall's TOP as a function of position along it.
 *
 * A wall drawn into a room meets the room's ceiling only if its top follows
 * that ceiling: under a slope it slopes, under a soffit it steps down to the
 * soffit's underside. A flat top at the room's typical wall height stops short
 * under a raised ceiling and pokes through a lowered one.
 *
 * `profileUnderCeiling` reads that top off a room's ceiling facets along a
 * line; `profileOfOutline` reads it back off a wall's own elevation outline
 * (`polygonCorners`), so a face laid on part of the wall later — a room
 * split's piece edge — gets the same top; `outlineCorners` turns either into
 * the outline a wall or a face carries.
 *
 * A profile is `{ origin, dir, length, segments }`. `segments` are
 * `{ s0, s1, h0, h1 }`, contiguous over `[0, length]`, with `s` in metres
 * from `origin` along the unit `dir` (X-Z) and `h` the height above the
 * wall's BASE. Heights, never absolute tops: a face is seated on its room's
 * floor (`shared-lib/room-split/walls.js`), whatever level the wall it came
 * from was recorded at.
 *
 * Pure. Y is up; the floor plane is X-Z.
 */

const { isPointInsidePolygon } = require('./pointInPolygon');
const { planeOf, isUpright, yOnPlane } = require('./facetPlane');

const EPS_S = 1e-6; // metres: breakpoints closer than this are one
// A stretch of top shorter than this is no stretch: where a wall crosses a
// soffit's drop, the bands either side meet on it, and the hairline between
// them read as a spike up to the ceiling behind.
const MIN_STRETCH_M = 1e-3;

/** The facets that can be the ceiling over a point: `{ ring, plane }`. */
function overheadFacets(ceilings) {
  const out = [];
  for (const c of ceilings || []) {
    const corners = c?.polygonCorners;
    if (!Array.isArray(corners) || corners.length < 3) continue;
    const plane = planeOf(corners);
    if (!plane || isUpright(plane)) continue;
    out.push({ ring: corners.map((p) => ({ x: p.x, z: p.z })), plane });
  }
  return out;
}

const cross = (u, v) => u.x * v.z - u.z * v.x;

/** Where on a → b (as 0..1) the ring's edges cross it. */
function ringCrossings(a, b, ring) {
  const d = { x: b.x - a.x, z: b.z - a.z };
  const out = [];
  for (let i = 0; i < ring.length; i++) {
    const p = ring[i];
    const q = ring[(i + 1) % ring.length];
    const e = { x: q.x - p.x, z: q.z - p.z };
    const den = cross(d, e);
    if (Math.abs(den) < 1e-12) continue;
    const ap = { x: p.x - a.x, z: p.z - a.z };
    const t = cross(ap, e) / den;
    const w = cross(ap, d) / den;
    if (t >= 0 && t <= 1 && w >= -1e-12 && w <= 1 + 1e-12) out.push(t);
  }
  return out;
}

/** Join neighbours that continue one another: same height, same slope. */
function mergeSegments(segments) {
  const out = [];
  for (const seg of segments) {
    const prev = out[out.length - 1];
    if (prev) {
      const slope = (g) => (g.h1 - g.h0) / (g.s1 - g.s0);
      if (
        Math.abs(prev.h1 - seg.h0) < 1e-9 &&
        Math.abs(slope(prev) - slope(seg)) < 1e-9
      ) {
        prev.s1 = seg.s1;
        prev.h1 = seg.h1;
        continue;
      }
    }
    out.push({ ...seg });
  }
  return out;
}

/** Sorted, with values closer than `eps` folded into one. */
function uniqueSorted(values, eps) {
  const s = [...values].sort((p, q) => p - q);
  return s.filter((v, i) => i === 0 || v - s[i - 1] > eps);
}

/**
 * The top a wall from `a` to `b` needs to meet the ceiling above it: at every
 * point, the LOWEST ceiling facet over it — a soffit's underside hangs below
 * the main plane, which the generator runs on across the soffit's band
 * (`estimate/ceilingAreas.js`). Where no facet covers the line, `height`, as a
 * wall with no ceiling to meet has always stood.
 *
 * @param {object[]} ceilings a room's `ceilings`
 * @param {{x,z}} a
 * @param {{x,z}} b
 * @param {{ floorY: number, height: number }} base the wall's floor, and the
 *   height it keeps where there is no ceiling over it
 * @returns {object} a profile (see the file comment)
 */
function profileUnderCeiling(ceilings, a, b, { floorY, height }) {
  const length = Math.hypot(b.x - a.x, b.z - a.z);
  const dir =
    length > 0
      ? { x: (b.x - a.x) / length, z: (b.z - a.z) / length }
      : { x: 1, z: 0 };
  const flat = {
    origin: { x: a.x, z: a.z },
    dir,
    length,
    segments: [{ s0: 0, s1: length, h0: height, h1: height }],
  };
  if (!(length > EPS_S)) return flat;
  const facets = overheadFacets(ceilings);
  if (facets.length === 0) return flat;

  const at = (u) => ({ x: a.x + u * (b.x - a.x), z: a.z + u * (b.z - a.z) });
  // Each facet's height along the line is linear in u: y(u) = ya + u (yb - ya).
  const lines = facets.map((f) => {
    const ya = yOnPlane(f.plane, a.x, a.z);
    return { ya, dy: yOnPlane(f.plane, b.x, b.z) - ya };
  });
  const us = [0, 1];
  facets.forEach((f) => us.push(...ringCrossings(a, b, f.ring)));
  // Two planes over the same stretch can swap which is lower part way along.
  for (let i = 0; i < lines.length; i++) {
    for (let j = i + 1; j < lines.length; j++) {
      const den = lines[i].dy - lines[j].dy;
      if (Math.abs(den) < 1e-12) continue;
      const u = (lines[j].ya - lines[i].ya) / den;
      if (u > 0 && u < 1) us.push(u);
    }
  }
  const breaks = uniqueSorted(
    us.filter((u) => u >= 0 && u <= 1),
    MIN_STRETCH_M / length
  );
  // The last stretch runs to the end, however close the last crossing was.
  if (breaks[breaks.length - 1] < 1) breaks[breaks.length - 1] = 1;

  const segments = [];
  for (let k = 0; k + 1 < breaks.length; k++) {
    const u0 = breaks[k];
    const u1 = breaks[k + 1];
    const mid = at((u0 + u1) / 2);
    let lowest = -1;
    let lowestY = Infinity;
    facets.forEach((f, i) => {
      if (!isPointInsidePolygon(mid, f.ring)) return;
      const y = lines[i].ya + ((u0 + u1) / 2) * lines[i].dy;
      if (y > floorY && y < lowestY) {
        lowestY = y;
        lowest = i;
      }
    });
    const h = (u) =>
      lowest < 0 ? height : lines[lowest].ya + u * lines[lowest].dy - floorY;
    segments.push({ s0: u0 * length, s1: u1 * length, h0: h(u0), h1: h(u1) });
  }
  return { ...flat, segments: mergeSegments(segments) };
}

/**
 * A wall's top read back off its own elevation outline: at every point along
 * the wall, the highest edge of the outline over it, as a height above the
 * outline's lowest point. The wall's axis is its outline's longest run in
 * plan, so a wall needs no rotation to be read.
 *
 * @returns {object|null} a profile, or null when the wall has no outline with
 *   any height to it
 */
function profileOfOutline(wall) {
  const corners = wall?.polygonCorners;
  if (!Array.isArray(corners) || corners.length < 3) return null;
  let i0 = 0;
  let i1 = 0;
  let far = 0;
  for (let i = 0; i < corners.length; i++) {
    for (let j = i + 1; j < corners.length; j++) {
      const d = Math.hypot(
        corners[j].x - corners[i].x,
        corners[j].z - corners[i].z
      );
      if (d > far) {
        far = d;
        i0 = i;
        i1 = j;
      }
    }
  }
  if (!(far > EPS_S)) return null;
  const dir = {
    x: (corners[i1].x - corners[i0].x) / far,
    z: (corners[i1].z - corners[i0].z) / far,
  };
  const o = corners[i0];
  const pts = corners.map((p) => ({
    s: (p.x - o.x) * dir.x + (p.z - o.z) * dir.z,
    y: p.y,
  }));
  const base = Math.min(...pts.map((p) => p.y));
  const ss = uniqueSorted(
    pts.map((p) => p.s),
    EPS_S
  );
  const sMin = ss[0];
  const segments = [];
  for (let k = 0; k + 1 < ss.length; k++) {
    const s0 = ss[k];
    const s1 = ss[k + 1];
    const sm = (s0 + s1) / 2;
    let best = null;
    let bestY = -Infinity;
    for (let i = 0; i < pts.length; i++) {
      const p = pts[i];
      const q = pts[(i + 1) % pts.length];
      if (Math.abs(q.s - p.s) < EPS_S) continue; // upright edge
      if (sm < Math.min(p.s, q.s) || sm > Math.max(p.s, q.s)) continue;
      const y = p.y + ((sm - p.s) * (q.y - p.y)) / (q.s - p.s);
      if (y > bestY) {
        bestY = y;
        best = { p, q };
      }
    }
    if (!best) continue;
    const yAt = (s) =>
      best.p.y +
      ((s - best.p.s) * (best.q.y - best.p.y)) / (best.q.s - best.p.s);
    segments.push({
      s0: s0 - sMin,
      s1: s1 - sMin,
      h0: yAt(s0) - base,
      h1: yAt(s1) - base,
    });
  }
  if (!segments.some((g) => g.h0 > EPS_S || g.h1 > EPS_S)) return null;
  return {
    origin: { x: o.x + sMin * dir.x, z: o.z + sMin * dir.z },
    dir,
    length: ss[ss.length - 1] - sMin,
    segments: mergeSegments(segments),
  };
}

/**
 * The profile's height at `s`, read on the segment to its `side` (+1 the
 * segment after s, -1 the one before) — the two differ only at a step. Past
 * either end the end segment runs on: a drawn wall's outline stops at its
 * taps, and the face on the outside of a bend runs past the tap to the mitre.
 */
function heightAt(profile, s, side) {
  const segs = profile.segments;
  let seg;
  if (s <= EPS_S && side < 0) seg = segs[0];
  else if (s >= profile.length - EPS_S && side > 0) seg = segs[segs.length - 1];
  else if (side > 0)
    seg = segs.find((g) => s < g.s1 - EPS_S) || segs[segs.length - 1];
  else seg = [...segs].reverse().find((g) => s > g.s0 + EPS_S) || segs[0];
  const t = seg.s1 > seg.s0 ? (s - seg.s0) / (seg.s1 - seg.s0) : 0;
  return seg.h0 + t * (seg.h1 - seg.h0);
}

/**
 * The outline of a wall face laid on `a` → `b` with the profile's top, seated
 * on `floorY`: bottom-left, bottom-right, then the top from b back to a — the
 * corner order a room wall's `polygonCorners` already use, two points at the
 * same place wherever the top steps.
 *
 * @returns {{x,y,z}[]}
 */
function outlineCorners(profile, a, b, floorY) {
  const { origin, dir } = profile;
  const sOf = (p) => (p.x - origin.x) * dir.x + (p.z - origin.z) * dir.z;
  const sa = sOf(a);
  const sb = sOf(b);
  const bottom = [
    { x: a.x, y: floorY, z: a.z },
    { x: b.x, y: floorY, z: b.z },
  ];
  if (Math.abs(sb - sa) < EPS_S) {
    const h = heightAt(profile, sa, 1);
    return [
      ...bottom,
      { x: b.x, y: floorY + h, z: b.z },
      { x: a.x, y: floorY + h, z: a.z },
    ];
  }
  const walk = sa > sb ? 1 : -1; // from b towards a, along s
  const point = (s, h) => {
    const t = (s - sa) / (sb - sa);
    return {
      x: a.x + t * (b.x - a.x),
      y: floorY + h,
      z: a.z + t * (b.z - a.z),
    };
  };
  const lo = Math.min(sa, sb) + EPS_S;
  const hi = Math.max(sa, sb) - EPS_S;
  const steps = profile.segments
    .slice(1)
    .map((g) => g.s0)
    .filter((s) => s > lo && s < hi)
    .sort((p, q) => (p - q) * walk);
  const top = [point(sb, heightAt(profile, sb, walk))];
  for (const s of steps) {
    const before = heightAt(profile, s, -walk);
    const after = heightAt(profile, s, walk);
    top.push(point(s, before));
    if (Math.abs(after - before) > 1e-9) top.push(point(s, after));
  }
  top.push(point(sa, heightAt(profile, sa, -walk)));
  return [...bottom, ...top];
}

/** The profile's tallest height: a wall's `dimensions.y` under it. */
const profileTop = (profile) =>
  Math.max(...profile.segments.flatMap((g) => [g.h0, g.h1]));

module.exports = {
  profileUnderCeiling,
  profileOfOutline,
  outlineCorners,
  profileTop,
};
